import { PropsWithChildren } from 'react'
import {
  createInstance,
  logOnlyEventDispatcher,
  OptimizelyProvider
} from '@optimizely/react-sdk'
import { COOKIE_KEY_FCID, getCookie } from 'utils/cookie'
import { getOptimizelyTrackingAttribute } from 'utils/tracking'
import optimizelyErrorHandler from 'utils/tracking/optimizely/errorHandler'
import isNotAffiliate from 'utils/formDependencies/conditions/isNotAffiliate'
import { useData } from 'stores/utils/hooks/useData'
import { useUrlParams } from 'stores/utils/hooks/useUrlParams'
import { isApp } from 'utils/formDependencies/conditions/isApp'
import { isE2ETesting } from 'utils/test/isE2ETesting'

// Relies on continous internet connection in contrast to the datafile approach
export const optimizelyClient = createInstance({
  sdkKey: process.env.REACT_APP_OPTIMIZELY_SDK_KEY,
  errorHandler: optimizelyErrorHandler,
  eventDispatcher: logOnlyEventDispatcher
})

export enum Experiments {
  CAT_3614_CAPTCHA_KILL_SWITCH = 'cat-3614_kill_switch',
  CAT_3494_DAC_KILL_SWITCH = '2025-07-25-cat-dac-kill-switch-flag',
  CAT_4558_RCE_SMAVA_VOUCHER_SALABLE = '2026-07-17-cat-4558-rce-smava-voucher-banner-for-salable-user',
  CAT_4558_RCE_SMAVA_VOUCHER_ACCEPTED = '2026-07-17-cat-4558-rce-smava-voucher-banner-for-accepted-user',
  CAT_4561_PROGRESSIVE_DISCLOSURE_FFG = '2026-07-14-cat-4561-progressive-disclosure',
  CAT_4784_DAC_SKIP_BUTTON_V5 = '2026-09-09-cat-4784-dac-skip-button-v5'
}

const CustomOptimizelyProvider = ({ children }: PropsWithChildren<unknown>) => {
  const urlParams = useUrlParams()

  const { data } = useData()

  const isOptimizelyDisabled =
    !isNotAffiliate(urlParams?.context) ||
    isApp(urlParams?.isAppContext) ||
    isE2ETesting()

  if (isOptimizelyDisabled) {
    return <>{children}</>
  }

  const userId = getCookie(COOKIE_KEY_FCID) || ''

  optimizelyClient.setUser({
    id: userId,
    attributes: { ...getOptimizelyTrackingAttribute(data) }
  })

  optimizelyClient
    .onReady()
    .then(() => (
      <OptimizelyProvider optimizely={optimizelyClient}>
        {children}
      </OptimizelyProvider>
    ))

  return <>{children}</>
}

export default CustomOptimizelyProvider
